// src/components/ProductReviews.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STARS = (n, size=14) => (
  <span style={{fontSize:size,letterSpacing:1}}>
    {[1,2,3,4,5].map(i=><span key={i} style={{color:i<=n?'#f59e0b':'#eadcf8'}}>★</span>)}
  </span>
);

export default function ProductReviews({ productId }) {
  const { user }    = useAuth();
  const [data,      setData]      = useState({ reviews:[], stats:{avg_rating:0,total:0,distribution:{5:0,4:0,3:0,2:0,1:0}} });
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [submitting,setSubmitting]= useState(false);
  const [form, setForm] = useState({ rating:0, hoverRating:0, title:'', review:'' });

  const fetchReviews = () => {
    setLoading(true);
    api.get(`/reviews?slug=${productId}`)
      .then(r => setData(r.data))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(() => { if (productId) fetchReviews(); }, [productId]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.rating) { toast.error('Please select a star rating'); return; }
    if (!form.review.trim()) { toast.error('Please write a review'); return; }
    setSubmitting(true);
    try {
      await api.post('/reviews', { product_id:productId, rating:form.rating, title:form.title, review:form.review });
      toast.success('Review submitted! 🎉');
      setShowForm(false);
      setForm({ rating:0, hoverRating:0, title:'', review:'' });
      fetchReviews();
    } catch(err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const { reviews, stats } = data;

  return (
    <div style={{marginTop:16}}>
      {/* ── Rating Summary ── */}
      <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:24,padding:24,background:'#f6efff',border:'1px solid var(--border)',marginBottom:24,alignItems:'center'}}>
        <div style={{textAlign:'center',padding:'0 16px'}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:56,color:'#7c3aed',lineHeight:1,fontWeight:700}}>{stats.avg_rating||'—'}</div>
          <div style={{margin:'8px 0'}}>{STARS(Math.round(stats.avg_rating), 18)}</div>
          <div style={{fontSize:12,color:'#64748b',fontFamily:"'Jost',sans-serif"}}>{stats.total} review{stats.total!==1?'s':''}</div>
        </div>
        <div>
          {[5,4,3,2,1].map(star => {
            const count = stats.distribution?.[star] || 0;
            const pct   = stats.total ? Math.round((count/stats.total)*100) : 0;
            return (
              <div key={star} style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                <span style={{fontSize:11,color:'#64748b',width:8,fontFamily:"'Jost',sans-serif"}}>{star}</span>
                <span style={{color:'#f59e0b',fontSize:12}}>★</span>
                <div style={{flex:1,height:6,background:'#eadcf8',borderRadius:3,overflow:'hidden'}}>
                  <div style={{height:'100%',background:'#f59e0b',width:`${pct}%`,borderRadius:3,transition:'width 0.5s'}}/>
                </div>
                <span style={{fontSize:11,color:'#64748b',width:28,textAlign:'right',fontFamily:"'Jost',sans-serif"}}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Write Review Button ── */}
      {user && !showForm && (
        <button onClick={()=>setShowForm(true)} className="btn btn-outline" style={{marginBottom:24,fontSize:11}}>
          ✍️ Write a Review
        </button>
      )}
      {!user && (
        <div style={{marginBottom:24,padding:14,background:'#f6efff',border:'1px dashed var(--border)',fontSize:13,color:'#64748b',fontFamily:"'Cormorant Garamond',serif",fontStyle:'italic',textAlign:'center'}}>
          <a href="/login" style={{color:'#7c3aed',fontWeight:600}}>Login</a> to write a review
        </div>
      )}

      {/* ── Review Form ── */}
      {showForm && (
        <form onSubmit={handleSubmit} style={{background:'#fff',border:'1px solid var(--border)',padding:24,marginBottom:24}}>
          <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:'#31263f',marginBottom:16}}>Write Your Review</h3>

          {/* Star selector */}
          <div style={{marginBottom:16}}>
            <div className="form-label">Your Rating *</div>
            <div style={{display:'flex',gap:4}}>
              {[1,2,3,4,5].map(star=>(
                <button key={star} type="button"
                  onMouseEnter={()=>setForm(f=>({...f,hoverRating:star}))}
                  onMouseLeave={()=>setForm(f=>({...f,hoverRating:0}))}
                  onClick={()=>setForm(f=>({...f,rating:star}))}
                  style={{fontSize:32,color:(form.hoverRating||form.rating)>=star?'#f59e0b':'#eadcf8',background:'none',border:'none',cursor:'pointer',padding:'0 2px',transition:'color 0.15s',lineHeight:1}}>
                  ★
                </button>
              ))}
              <span style={{fontSize:13,color:'#64748b',marginLeft:8,alignSelf:'center',fontFamily:"'Jost',sans-serif"}}>
                {['','Poor','Fair','Good','Very Good','Excellent'][form.rating||form.hoverRating]||''}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Review Title</label>
            <input className="form-input" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Sum up your experience in a line"/>
          </div>
          <div className="form-group">
            <label className="form-label">Your Review *</label>
            <textarea className="form-input" value={form.review} onChange={e=>setForm(f=>({...f,review:e.target.value}))} placeholder="What did you think about the saree? Quality, colour, weaving, delivery…" style={{minHeight:100,resize:'vertical'}}/>
          </div>

          <div style={{display:'flex',gap:10}}>
            <button type="button" onClick={()=>setShowForm(false)} className="btn btn-outline" style={{flex:1,justifyContent:'center',padding:12,fontSize:11}}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{flex:2,justifyContent:'center',padding:12,fontSize:11}} disabled={submitting}>{submitting?'Submitting...':'Submit Review'}</button>
          </div>
        </form>
      )}

      {/* ── Review List ── */}
      {loading ? <div className="spinner"/> : reviews.length === 0 ? (
        <div style={{textAlign:'center',padding:'32px 0',color:'#64748b',fontFamily:"'Cormorant Garamond',serif",fontSize:17,fontStyle:'italic'}}>
          No reviews yet. Be the first to share your experience!
        </div>
      ) : (
        <div>
          {reviews.map(r => (
            <div key={r.id} className="review-card" style={{marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8,marginBottom:8}}>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:36,height:36,borderRadius:'50%',background:'#7c3aed',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Playfair Display',serif",fontSize:16,flexShrink:0}}>
                      {r.display_name[0].toUpperCase()}
                    </div>
                    <div>
                      <span className="review-card-name">{r.display_name}</span>
                      {r.verified===1 && <span className="review-verified" style={{marginLeft:8}}>✓ Verified Purchase</span>}
                    </div>
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  {STARS(r.rating)}
                  <div className="review-card-date">{new Date(r.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
                </div>
              </div>
              {r.title && <div style={{fontFamily:"'Jost',sans-serif",fontWeight:600,fontSize:14,color:'#31263f',marginBottom:4}}>{r.title}</div>}
              <div className="review-card-text">{r.review}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
